import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type BaseLayer from 'ol/layer/Base';
import VectorSource from 'ol/source/Vector';
import { useMapStore } from '@/store/map.store';
import workspaceService from '@/services/workspace.service';
import { show } from '@/utils/companyLayers';
import { useToast } from '@/components/common/Toast';
import { useEditingStore } from './editing.store';
import { editApi } from './editingApi';
import EditingWorkbench from './EditingWorkbench';
import type { LayerShape } from './EditSession';

/**
 * The editing tools on the app's map, while a company layer is being drawn into ({@link useEditingStore}). The layer
 * is switched on if it was not; its own features (the source the map already reads it from) are what is edited, so
 * what is saved is drawn by the layer's own style. A layer drawn as a heatmap or as clusters is hidden while it is
 * edited: its features are shown one by one instead, so that each can be picked.
 */
export const EditingHost: React.FC = () => {
  const layer = useEditingStore((s) => s.layer);
  const stop = useEditingStore((s) => s.stop);
  const map = useMapStore((s) => s.mapInstance);
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').substring(0, 2);
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<{ source: VectorSource; olLayer: BaseLayer } | null>(null);

  const { data: full } = useQuery({
    queryKey: ['workspaceLayer', layer?.workspace, layer?.key],
    queryFn: () => workspaceService.layer(layer!.workspace, layer!.key),
    enabled: !!layer,
  });

  useEffect(() => {
    setTarget(null);
    if (!map || !layer) return;
    show(map, layer);
    let found: BaseLayer | undefined;
    map.getLayers().forEach((l) => {
      if (l.get('name') === layer.name) found = l;
    });
    const source = found?.get('featureSource');
    if (found && source instanceof VectorSource) {
      useMapStore.getState().setCurrentTool('pan');
      setTarget({ source, olLayer: found });
    }
  }, [map, layer]);

  const api = useMemo(() => (layer ? editApi(layer.workspace, layer.key) : null), [layer]);

  if (!layer || !map || !target || !api || !full) return null;
  const title = layer.labels[lang] || layer.labels.en || layer.key;
  return (
    <EditingWorkbench
      key={layer.name}
      map={map}
      source={target.source}
      shape={(layer.geometry ?? 'any') as LayerShape}
      title={title}
      fields={full.fields ?? []}
      api={api}
      lang={lang}
      hideLayer={target.olLayer.get('companyKind') === 'vector' ? undefined : target.olLayer}
      onClose={stop}
      onSaved={() => {
        queryClient.invalidateQueries({ queryKey: ['workspaceLayers', layer.workspace] });
        queryClient.invalidateQueries({ queryKey: ['workspaceLayers', 'all'] });
      }}
      notify={(kind, text) => { if (kind !== 'info') addToast(text, kind, kind === 'error' ? 8000 : 4000); }}
    />
  );
};

export default EditingHost;
